import { FlyerTemplatePosition } from "./flyer-template-position.interface";
import { Grid } from "../grid/grid.interface";
import {
  getHoveredPositionColour,
  getUnhoveredPositionColour,
} from "../../../../pages/flyer-template-creator/types/position-colour/position-colour.functions";
import { PositionColour } from "../../../../pages/flyer-template-creator/types/position-colour/position-colour.interface";
import { XyCoordinates } from "../xy-coordinates.interface";
import { Canvas } from "../canvas/canvas.interface";
import {
  getCellHeight,
  getCellWidth,
  getColumnPosition,
  getColumnSpanWidth,
  getRowPosition,
  getRowSpanHeight,
} from "../grid/grid.functions";
import { Range } from "../../../../../../types/range/range";
import { Rectangle } from "../rectangle.interface";
import { FlyerTemplate } from "../flyer-template/flyer-template.interface";
import { DragContext } from "../drag-context.interface";
import { FlyerTemplatePositionEditForm } from "../flyer-template-position-edit-form/flyer-template-position-edit-form.interface";
import { Nullable } from "../../../../../../types/common";

const DragToleranceRatio = 0.25;

export const getFlyerTemplatePositionX = (position: FlyerTemplatePosition, grid: Grid): number =>
  position.columns.min === 0 ? 0 : grid.columns[position.columns.min - 1].x;

export const getFlyerTemplatePositionY = (position: FlyerTemplatePosition, grid: Grid): number =>
  position.rows.min === 0 ? 0 : grid.rows[position.rows.min - 1].y;

export const getFlyerTemplatePositionColour = (position: FlyerTemplatePosition): PositionColour =>
  position.selected ? getHoveredPositionColour() : getUnhoveredPositionColour(position);

export function draggedPositionCollidesWithConsumedCells(context: DragContext): boolean {
  const occupiedCells = findOccupiedCells(context.grid, context.template).filter(
    (cell) => !positionOccupiesCell(context.position, cell)
  );

  const movedPosition = createMovedPositionRectangle(
    context.position,
    context.dragPosition,
    context.grid,
    context.canvas
  );
  return Boolean(occupiedCells.find(hasCollision(movedPosition)));
}

export function getFlyerTemplatePositionDragBoundaries(context: DragContext): XyCoordinates {
  const boundaries = { ...context.dragPosition };

  const positionX = getFlyerTemplatePositionX(context.position, context.grid);
  const positionWidth = getColumnSpanWidth(context.position.columns, context.grid, context.canvas);

  const positionY = getFlyerTemplatePositionY(context.position, context.grid);
  const positionHeight = getRowSpanHeight(context.position.rows, context.grid, context.canvas);

  if (positionX + context.dragPosition.x <= 0) {
    boundaries.x = positionX * -1;
  }

  if (positionX + positionWidth + context.dragPosition.x >= context.canvas.width) {
    boundaries.x = context.canvas.width - positionWidth - positionX;
  }

  if (positionY + context.dragPosition.y <= 0) {
    boundaries.y = positionY * -1;
  }

  if (positionY + positionHeight + context.dragPosition.y >= context.canvas.height) {
    boundaries.y = context.canvas.height - positionHeight - positionY;
  }

  return boundaries;
}

function cellHasBlock(cell: Rectangle, template: FlyerTemplate): boolean {
  for (const position of template.positions) {
    if (positionOccupiesCell(position, cell)) {
      return true;
    }
  }
  return false;
}

function positionOccupiesCell(position: FlyerTemplatePosition, cell: Rectangle): boolean {
  return (
    positionOccupiesColumn(position, getColumnPosition(cell)) &&
    positionOccupiesRow(position, getRowPosition(cell))
  );
}

function positionOccupiesRow(position: FlyerTemplatePosition, rowIndex: number): boolean {
  return position.rows.has(rowIndex);
}

function positionOccupiesColumn(position: FlyerTemplatePosition, columnIndex: number): boolean {
  return position.columns.has(columnIndex);
}

function createMovedPositionRectangle(
  position: FlyerTemplatePosition,
  pos: XyCoordinates,
  grid: Grid,
  canvas: Canvas
): Rectangle {
  const x1 = getFlyerTemplatePositionX(position, grid) + pos.x;
  const x2 = x1 + getColumnSpanWidth(position.columns, grid, canvas);

  const y1 = getFlyerTemplatePositionY(position, grid) + pos.y;
  const y2 = y1 + getRowSpanHeight(position.rows, grid, canvas);

  return { x1, x2, y1, y2 };
}

const hasCollision = (a: Rectangle) => (b: Rectangle): boolean => {
  const addTolerance = (span: number) => span - span * 0.15;

  const aWidth = addTolerance(a.x2 - a.x1);
  const bWidth = addTolerance(b.x2 - b.x1);

  const aHeight = addTolerance(a.y2 - a.y1);
  const bHeight = addTolerance(b.y2 - b.y1);

  return (
    a.x1 + aWidth >= b.x1 &&
    a.x1 <= b.x1 + bWidth &&
    a.y1 + aHeight >= b.y1 &&
    a.y1 <= b.y1 + bHeight
  );
};

function findOccupiedCells(grid: Grid, template: FlyerTemplate): Rectangle[] {
  return grid.cells!.filter((cell) => cellHasBlock(cell, template));
}

export const getDraggedPositionRowRange = (
  deltaY: number,
  position: FlyerTemplatePosition,
  grid: Grid
): Range => {
  const positionY = getFlyerTemplatePositionY(position, grid) + deltaY;

  for (const cell of grid.cells || []) {
    if (positionYDragPosIntersectsCell(positionY, cell)) {
      return calculateNewRowRange(position, cell);
    }
  }

  return position.rows;
};

export const getDraggedBlockColumnRange = (
  deltaX: number,
  position: FlyerTemplatePosition,
  grid: Grid
): Range => {
  const positionX = getFlyerTemplatePositionX(position, grid) + deltaX;

  for (const cell of grid.cells || []) {
    if (positionXDragPosIntersectsCell(positionX, cell)) {
      return calculateNewColumnRange(position, cell);
    }
  }

  return position.columns;
};

function positionYDragPosIntersectsCell(positionY: number, cell: Rectangle): boolean {
  const yTolerance = getCellHeight(cell) * DragToleranceRatio;
  return positionY >= cell.y1 - yTolerance && positionY <= cell.y2 - yTolerance;
}

function positionXDragPosIntersectsCell(positionX: number, cell: Rectangle): boolean {
  const xTolerance = getCellWidth(cell) * DragToleranceRatio;
  return positionX >= cell.x1 - xTolerance && positionX <= cell.x2 - xTolerance;
}

function calculateNewColumnRange(position: FlyerTemplatePosition, cell: Rectangle): Range {
  const cellXDelta = getColumnPosition(cell) - position.columns.min;
  return new Range([
    Math.round(getColumnPosition(cell)),
    Math.round(position.columns.max + cellXDelta),
  ]);
}

function calculateNewRowRange(position: FlyerTemplatePosition, cell: Rectangle): Range {
  const cellYDelta = getRowPosition(cell) - position.rows.min;

  return new Range([Math.round(getRowPosition(cell)), Math.round(position.rows.max + cellYDelta)]);
}

export function createFlyerTemplatePosition(
  rows: Range,
  columns: Range,
  positions: FlyerTemplatePosition[]
): FlyerTemplatePosition {
  return {
    id: findMaxPositionId(positions) + 1,
    rows,
    columns,
    label: "",
    type: "default",
  };
}

function findMaxPositionId(positions: FlyerTemplatePosition[]): number {
  return positions.reduce((max, position) => (position.id > max ? position.id : max), 0);
}

export function findPositionById(
  positionId: number,
  positions: FlyerTemplatePosition[]
): Nullable<FlyerTemplatePosition> {
  return positions.find((position) => position.id === positionId) || null;
}

export function updatePosition(
  position: FlyerTemplatePosition,
  form: FlyerTemplatePositionEditForm
): FlyerTemplatePosition {
  return {
    ...position,
    label: form.label,
    type: form.type,
  };
}
